/* Falconclaw PTT - volume deal nudge (2026-08-11, rev 2)
   Drives snippets/fc-ptt-volume-deal.liquid on the FC PTT product page.

   rev 2 (same day, Ivo's review): the widget is minimalist now - no panel,
   no eyebrow, no counter - so this file no longer writes an eyebrow label,
   a unit counter or an is-unlocked class. It writes the message, the bar
   and the cart-split line, and nothing else.

   What it does: combines the on-page quantity selector with the units of this
   product already sitting in the cart (both colours, every variant), then tells
   the shopper in euros how far they are from the 4-unit / -10% volume deal.

   Combine rule: TOTAL = units of this product in /cart.js + units currently
   selected in the quantity box. That is "what you will have once you add this",
   which is the number the cart-level Shopify Function will actually see.

   Every price comes from /products/<handle>.js at runtime. Nothing is hand
   typed, so a price change on the product never leaves a stale euro figure on
   the page. Availability comes from the same payload: not available -> hide.

   The discount itself is a Shopify Function. This file only narrates it. */
(function () {
  'use strict';

  if (document.documentElement.dataset.fcVolBooted === '1') return;
  document.documentElement.dataset.fcVolBooted = '1';

  var SEL = '[data-fc-vol]';
  var PERCENT = 10;        /* overwritten from the block's data attribute */
  var product = null;      /* cached /products/<handle>.js payload */
  var cartUnits = 0;       /* units of this product already in the cart */
  var productPending = false;
  var cartPending = false;
  var renderQueued = false;

  /* ---------- helpers ---------- */

  function root() {
    return document.querySelector(SEL);
  }

  function intAttr(el, name, fallback) {
    var v = parseInt(el.getAttribute(name), 10);
    return isNaN(v) ? fallback : v;
  }

  function groupDots(intPart) {
    return intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }

  function groupCommas(intPart) {
    return intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /* Format cents with the store's own money format so the widget matches the
     price block above it (falconclaw.eu renders "EUR 1.234,56" style). */
  function money(cents) {
    var settings = window.themeVariables && window.themeVariables.settings;
    var format = (settings && settings.moneyFormat) || '€{{amount_with_comma_separator}}';
    var value = cents / 100;
    var parts = value.toFixed(2).split('.');
    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, function (whole, key) {
      switch (key) {
        case 'amount_with_comma_separator':
          return groupDots(parts[0]) + ',' + parts[1];
        case 'amount_no_decimals_with_comma_separator':
          return groupDots(String(Math.round(value)));
        case 'amount_no_decimals':
          return groupCommas(String(Math.round(value)));
        default:
          return groupCommas(parts[0]) + '.' + parts[1];
      }
    });
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* Euro figures get the accent colour, everything else stays plain text. */
  function withAmount(before, cents, after) {
    return escapeHtml(before) + '<b>' + escapeHtml(money(cents)) + '</b>' + escapeHtml(after);
  }

  /* ---------- reading the page ---------- */

  function quantityInput(el) {
    var scope = el.closest('.product-info') || document;
    return scope.querySelector('input.quantity-selector__input[name="quantity"]') ||
      document.querySelector('input.quantity-selector__input[name="quantity"]');
  }

  function selectedQuantity(el) {
    var input = quantityInput(el);
    if (!input) return 1;
    var n = parseInt(input.value, 10);
    return isNaN(n) || n < 0 ? 1 : n;
  }

  function selectedVariantId(el) {
    var match = window.location.search.match(/[?&]variant=(\d+)/);
    if (match) return match[1];
    var scope = el.closest('.product-info') || document;
    var input = scope.querySelector('[name="id"]') || document.querySelector('[name="id"]');
    return input && input.value ? String(input.value) : '';
  }

  function unitPriceCents(el) {
    if (!product) return 0;
    var id = selectedVariantId(el);
    var variants = product.variants || [];
    for (var i = 0; i < variants.length; i++) {
      if (String(variants[i].id) === id) return variants[i].price;
    }
    return product.price;
  }

  /* ---------- data ---------- */

  function loadProduct(handle) {
    if (product || productPending || !handle) return;
    productPending = true;
    fetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        productPending = false;
        if (data) { product = data; render(); }
      })
      .catch(function () { productPending = false; });
  }

  function loadCart(productId) {
    if (cartPending) return;
    cartPending = true;
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        cartPending = false;
        if (!cart) return;
        var units = 0;
        (cart.items || []).forEach(function (item) {
          if (String(item.product_id) === String(productId)) units += item.quantity;
        });
        cartUnits = units;
        render();
      })
      .catch(function () { cartPending = false; });
  }

  /* ---------- copy ---------- */

  function messageHtml(needed, threshold, total, perUnit) {
    if (needed >= 3) {
      return escapeHtml(needed + ' more unlocks -' + PERCENT +
        '% on every unit. Round up your teammates.');
    }
    if (needed === 2) {
      return withAmount('2 more and everyone saves ', perUnit, ' each.');
    }
    if (needed === 1) {
      return withAmount('ONE more PTT and you all save ', perUnit * threshold, ' total.');
    }
    return withAmount('Volume deal unlocked. You are saving ', perUnit * total, '.');
  }

  /* ---------- render ---------- */

  function render() {
    var el = root();
    if (!el) return;

    var handle = el.getAttribute('data-fc-vol-handle');
    var productId = el.getAttribute('data-fc-vol-product-id');
    var threshold = intAttr(el, 'data-fc-vol-threshold', 4);
    PERCENT = intAttr(el, 'data-fc-vol-percent', 10);

    if (!product) { loadProduct(handle); loadCart(productId); return; }

    /* Out of stock (or gone): the widget says nothing at all. */
    if (!product.available) { el.hidden = true; return; }
    el.hidden = false;

    var unit = unitPriceCents(el);
    if (!unit) return;

    var perUnit = Math.round((unit * PERCENT) / 100);
    var selected = selectedQuantity(el);
    var total = cartUnits + selected;
    var needed = threshold - total;

    var key = [total, cartUnits, selected, perUnit, threshold].join(':');
    if (el.dataset.fcVolKey === key) return;
    el.dataset.fcVolKey = key;

    el.style.setProperty('--fc-vol-p', String(Math.min(total / threshold, 1)));

    var msg = el.querySelector('[data-fc-vol-msg]');
    if (msg) {
      msg.innerHTML = messageHtml(needed, threshold, total, perUnit);
      msg.classList.remove('is-changed');
      /* force the keyframe to restart on every state change */
      void msg.offsetWidth;
      msg.classList.add('is-changed');
    }

    var split = el.querySelector('[data-fc-vol-split]');
    if (split) {
      if (cartUnits > 0) {
        split.textContent = cartUnits + ' already in cart, ' + selected + ' selected here.';
        split.hidden = false;
      } else {
        split.textContent = '';
        split.hidden = true;
      }
    }

    var bar = el.querySelector('[data-fc-vol-bar]');
    if (bar) {
      bar.setAttribute('aria-valuemax', String(threshold));
      bar.setAttribute('aria-valuenow', String(Math.min(total, threshold)));
    }
  }

  function queueRender() {
    if (renderQueued) return;
    renderQueued = true;
    setTimeout(function () { renderQueued = false; render(); }, 0);
  }

  function refreshCart() {
    var el = root();
    if (el) loadCart(el.getAttribute('data-fc-vol-product-id'));
  }

  /* ---------- wiring (document level, capture phase: Impact re-renders the
     buy box on variant change and swaps our node out) ---------- */

  function isQuantityField(target) {
    return target && target.matches && target.matches('input.quantity-selector__input[name="quantity"]');
  }

  document.addEventListener('input', function (e) {
    if (isQuantityField(e.target)) queueRender();
  }, true);

  document.addEventListener('change', function (e) {
    if (isQuantityField(e.target) || (e.target && e.target.name === 'id')) queueRender();
  }, true);

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    if (t.closest('.quantity-selector__button')) { queueRender(); return; }
    if (t.closest('[name="add"], button[type="submit"], [data-fc-add-to-cart]')) {
      setTimeout(refreshCart, 700);
      setTimeout(refreshCart, 1800);
    }
  }, true);

  document.addEventListener('cart:refresh', refreshCart);
  document.addEventListener('cart:change', refreshCart);

  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) refreshCart();
  });

  /* The header cart badge is the one signal every add path updates, whatever
     drawer or app did the adding. */
  function watchCartCount() {
    if (!window.MutationObserver) return;
    document.querySelectorAll('cart-count').forEach(function (node) {
      if (node.dataset.fcVolWatched === '1') return;
      node.dataset.fcVolWatched = '1';
      new MutationObserver(refreshCart).observe(node, {
        childList: true, subtree: true, characterData: true
      });
    });
  }

  function watchBody() {
    if (!window.MutationObserver || !document.body) return;
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var t = records[i].target;
        /* ignore our own writes, or we loop */
        if (t && t.closest && t.closest(SEL)) continue;
        watchCartCount();
        queueRender();
        return;
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  function boot() {
    watchCartCount();
    watchBody();
    render();
    refreshCart();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
